/**
 * Response Processor Registry
 *
 * Singleton registry for managing Response Processors.
 * Similar pattern to agentActionHandlers in /src/config/agentActions.ts
 *
 * @module services/responseProcessors/registry
 */

import { IResponseProcessor } from './types';

/**
 * Registry for Response Processors
 *
 * Manages registration and lookup of processors by agent ID.
 * Supports a default processor for agents without specialized handling.
 *
 * @example
 * ```typescript
 * // Register a processor
 * responseProcessorRegistry.register(new ReportBuilderProcessor());
 *
 * // Get processor for an agent
 * const processor = responseProcessorRegistry.getProcessor('report-builder');
 * ```
 */
class ResponseProcessorRegistry {
  private processors: Map<string, IResponseProcessor> = new Map();
  private defaultProcessor: IResponseProcessor | null = null;

  /**
   * Register a processor for one or more agent IDs
   *
   * @param processor - The processor to register
   * @returns True if registration succeeded, false if validation failed
   */
  register(processor: IResponseProcessor): boolean {
    // Validate agentIds is non-empty
    if (!processor.agentIds || processor.agentIds.length === 0) {
      return false;
    }

    // Validate each agentId is a non-empty string
    const invalidIds = processor.agentIds.filter(id => typeof id !== 'string' || id.trim() === '');
    if (invalidIds.length > 0) {
      return false;
    }

    for (const agentId of processor.agentIds) {
      this.processors.set(agentId, processor);
    }
    return true;
  }

  /**
   * Set the default processor for unregistered agents
   *
   * @param processor - The default processor
   */
  setDefault(processor: IResponseProcessor): void {
    this.defaultProcessor = processor;
  }

  /**
   * Get processor for an agent ID
   *
   * Falls back to default processor if no specific one registered.
   *
   * @param agentId - The agent ID to look up
   * @returns The processor for the agent, or null if none found
   */
  getProcessor(agentId: string): IResponseProcessor | null {
    return this.processors.get(agentId) ?? this.defaultProcessor;
  }

  /**
   * Check if a specific processor is registered for an agent
   *
   * @param agentId - The agent ID to check
   * @returns True if a specific processor (not default) is registered
   */
  hasProcessor(agentId: string): boolean {
    return this.processors.has(agentId);
  }

  /**
   * Get all registered agent IDs
   *
   * @returns Array of agent IDs with registered processors
   */
  getRegisteredAgentIds(): string[] {
    return Array.from(this.processors.keys());
  }

  /**
   * Get processor descriptions for debugging
   *
   * @returns Map of agent IDs to processor descriptions
   */
  getProcessorDescriptions(): Record<string, string> {
    const descriptions: Record<string, string> = {};
    for (const [agentId, processor] of this.processors.entries()) {
      descriptions[agentId] = processor.description;
    }
    if (this.defaultProcessor) {
      descriptions['*'] = this.defaultProcessor.description;
    }
    return descriptions;
  }

  /**
   * Clear all registrations (useful for testing)
   */
  clear(): void {
    this.processors.clear();
    this.defaultProcessor = null;
  }
}

// Singleton instance
export const responseProcessorRegistry = new ResponseProcessorRegistry();
